<template>
    <div class="section" ref="calendarView">
        <div class="hidden lg:block">
            <v-calendar
                v-if="isMounted"
                class="custom-calendar max-w-full"
                :masks="masks"
                :attributes="filteredEvents"
                :page-swipe="false"
                expanded
                :initial-page="defaultDate"
                @did-move="onDateChange"
                v-model="defaultDate"
            >
                <template v-slot:day-content="{ day, attributes }">
                    <CalendarDay :day="day" :attributes="attributes" />
                </template>
            </v-calendar>
        </div>
        <div class="lg:hidden">
            <v-calendar
                v-if="isMounted"
                :masks="masks"
                :attributes="filteredEvents"
                :page-swipe="false"
                @dayclick="showDayEvents"
                :initial-page="defaultDate"
                @did-move="onDateChange"
                expanded
            >
                <template #day="{ day, attributes }">
                    <CalendarDay :day="day" :attributes="attributes" />
                </template>
            </v-calendar>
            <div class="my-4" v-if="selectedDate">
                <h4 class="font-semibold" v-if="dayEvents.length">Training & Events on {{ formattedSelectedDate }}</h4>
                <h4 class="font-semibold" v-else>There is no Training & Events on {{ formattedSelectedDate }}</h4>
            </div>
            <ul class="mt-4" v-if="dayEvents.length">
                <li class="bg-white p-4 rounded shadow-md space-y-2" v-for="event in dayEvents" :key="event.id">
                    <div class="flex justify-between">
                        <div class="flex-auto space-y-2">
                            <h4 class="font-semibold">
                                <a :href="event.customData.url" class="text-blue-dark">{{ event.customData.title }}</a>
                            </h4>
                            <p class="text-sm text-gray-600"></p>
                            <p class="text-sm sm:text-base">
                                <span> {{ event.customData.formated_date.start_month }}&nbsp; </span>
                                <span> {{ event.customData.formated_date.start_day }} </span>,
                                <span>{{ event.customData.formated_date.year }}</span
                                >-<span>{{ event.customData.formated_date.start_hour }}</span>
                                <span>&nbsp;{{ event.customData.formated_date.timezone }}</span>
                            </p>
                        </div>
                    </div>
                    <div>
                        <p class="line-clamp-3">
                            {{ event.customData.summary }}
                        </p>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup>
import 'v-calendar/dist/style.css'
import CalendarDay from './CalendarDay.vue'
import { ref, computed, onMounted, nextTick } from 'vue'
import { toLocalTz } from '../../support/utils'

const dayEvents = ref([])
const selectedDate = ref(null)
const defaultDate = ref(null)
const isMounted = ref(false)
const calendarView = ref(null)
const currentUrl = ref(null)

const props = defineProps({
    events: Array,
    occurrence: String,
})

onMounted(() => {
    if (! 'URLSearchParams' in window) {
        alert('Your browser does not support our calendar. Please use a modern browser.')
        return
    }
    
    currentUrl.value = new URL(window.location.href)
    selectedDate.value = new Date()

    if (props.occurrence) {
        if (/^\d{4}-\d{2}$/.test(props.occurrence)) {
            selectedDate.value = new Date(props.occurrence + '-01T00:00')
        } else if (/^\d{4}-\d{2}-\d{2}$/.test(props.occurrence)) {
            selectedDate.value = new Date(props.occurrence + 'T00:00')
        } else {
            selectedDate.value = new Date(props.occurrence)
        }
    }

    defaultDate.value = { month: selectedDate.value.getMonth() + 1, year: selectedDate.value.getFullYear() }
    setOccurrenceQueryParam(defaultDate.value)    

    if (currentUrl.value.searchParams.has('scrollToCalendar')) {
        nextTick(() => calendarView.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
    
    isMounted.value = true
})

const formattedSelectedDate = computed(() => {
    return dayjs(selectedDate.value).format('D MMMM')
})

const masks = ref({ weekdays: 'WWW' })

const filteredEvents = computed(() => {
    return props.events.map((event) => {
        const startDate = toLocalTz(event.postable.start_date, true)
        return {
            key: event.id,
            dot: true,
            dates: new Date(startDate.year(), startDate.month(), startDate.date()),
            customData: {
                ...event.postable,
                formated_date: formatDate(event.postable.start_date),
            },
        }
    })
})

function showDayEvents(e) {
    const date = new Date(e.date)
    selectedDate.value = date
    dayEvents.value = filteredEvents.value.filter((item) => {
        return Number(item.customData.formated_date.start_day) === date.getDate()
    })
}

function formatDate(startDate) {
    const dt = toLocalTz(startDate, true)
    return {
        start_hour: dt.format('hh:mm A'),
        start_month: dt.format('MMMM'),
        start_day: dt.format('DD'),
        year: dt.format('YYYY'),
        timezone: dt.format('z'),
    }
}

const setOccurrenceQueryParam = (newDate) => {
    const newOccurrence = `${newDate.year}-${newDate.month < 10 ? '0' + newDate.month : newDate.month}`;
    const currentOccurrence = currentUrl.value.searchParams.get('filter[occurrence]')

    if (currentOccurrence !== newOccurrence) {
        currentUrl.value.searchParams.set('filter[occurrence]', newOccurrence)
        currentUrl.value.searchParams.set('filter[type]', 'calendar')
        window.history.pushState(null, '', currentUrl.value.toString())
    }
}

const onDateChange = (pages) => {
    setOccurrenceQueryParam({ month: pages[0].month, year: pages[0].year })
    currentUrl.value.searchParams.set('scrollToCalendar', 'true')
    window.location.search = currentUrl.value.searchParams.toString()
}
</script>
